import { useCallback, useEffect, useRef, useState } from "react";
import type { CityHistoryItemDto, CityHistoryResponseDto } from "../../shared/api";
import { MessageExternalLink } from "./MessageExternalLink";

interface CityHistoryProps {
  cityCode: string;
  cityName: string;
  currentLocalDate: string;
}

function dateLabel(localDate: string, currentLocalDate: string) {
  const formatted = new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    day: "numeric",
    month: "long",
    year: "numeric"
  }).format(new Date(`${localDate}T12:00:00+03:00`));
  return localDate === currentLocalDate ? `Bugün · ${formatted}` : formatted;
}

function timeLabel(publishedAt: string) {
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(publishedAt));
}

export function CityHistory({ cityCode, cityName, currentLocalDate }: CityHistoryProps) {
  const [items, setItems] = useState<CityHistoryItemDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestSequence = useRef(0);

  const load = useCallback(
    async (cursor: string | null, replace: boolean) => {
      const sequence = ++requestSequence.current;
      setLoading(true);
      setError(null);
      try {
        const query = new URLSearchParams({ limit: "20" });
        if (cursor) query.set("cursor", cursor);
        const response = await fetch(`/api/cities/${cityCode}/history?${query}`);
        if (!response.ok) throw new Error(`Geçmiş isteği başarısız (${response.status})`);
        const payload = (await response.json()) as CityHistoryResponseDto;
        if (sequence !== requestSequence.current) return;
        setItems((current) => {
          const combined = replace ? payload.items : [...current, ...payload.items];
          return Array.from(new Map(combined.map((item) => [item.messageId, item])).values());
        });
        setNextCursor(payload.nextCursor);
      } catch (historyError) {
        if (sequence !== requestSequence.current) return;
        setError(
          historyError instanceof Error ? historyError.message : "Şehir geçmişi yüklenemedi."
        );
      } finally {
        if (sequence === requestSequence.current) setLoading(false);
      }
    },
    [cityCode]
  );

  useEffect(() => {
    let active = true;
    queueMicrotask(() => {
      if (active) void load(null, true);
    });
    return () => {
      active = false;
      requestSequence.current += 1;
    };
  }, [load]);

  const groups = items.reduce<Array<{ localDate: string; items: CityHistoryItemDto[] }>>(
    (result, item) => {
      const current = result.at(-1);
      if (current?.localDate === item.localDate) current.items.push(item);
      else result.push({ localDate: item.localDate, items: [item] });
      return result;
    },
    []
  );

  return (
    <section className="city-history" aria-labelledby={`history-${cityCode}`}>
      <div className="city-history__heading">
        <div>
          <span className="panel-index">Şehir arşivi</span>
          <h3 id={`history-${cityCode}`}>{cityName} geçmişi</h3>
        </div>
        <button
          className="text-button"
          type="button"
          onClick={() => void load(null, true)}
          disabled={loading}
        >
          Yenile
        </button>
      </div>

      {loading && items.length === 0 && <p className="history-note">Geçmiş yükleniyor…</p>}
      {error && (
        <div className="history-error" role="alert">
          <p>{error}</p>
          <button className="text-button" type="button" onClick={() => void load(null, true)}>
            Yeniden dene
          </button>
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <p className="history-note">Bu şehrin henüz ücretli mesaj geçmişi yok.</p>
      )}

      <div className="history-groups">
        {groups.map((group) => (
          <section className="history-day" key={group.localDate}>
            <h4>{dateLabel(group.localDate, currentLocalDate)}</h4>
            <div className="history-day__items">
              {group.items.map((item) => (
                <article className="history-message" key={item.messageId}>
                  <blockquote>{item.message}</blockquote>
                  <div className="history-message__meta">
                    <span>{item.nickname ? `— ${item.nickname}` : "İsimsiz"}</span>
                    <span>
                      Sürüm {item.version} ·{" "}
                      <time dateTime={item.publishedAt}>{timeLabel(item.publishedAt)}</time>
                    </span>
                  </div>
                  {item.url && <MessageExternalLink url={item.url} />}
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>

      {nextCursor && (
        <button
          className="history-more"
          type="button"
          onClick={() => void load(nextCursor, false)}
          disabled={loading}
        >
          {loading ? "Yükleniyor…" : "Daha eski mesajlar"}
        </button>
      )}
    </section>
  );
}
